import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SupplierSelector from "@/components/inventory/SupplierSelector";
import SupplierModal from "@/components/inventory/SupplierModal";
import { createEntry, apiRequest, createProduct } from "@/lib/api";
import { getCurrentUser, getCurrentShop } from "@/lib/auth";
import { PRODUCT_CATEGORIES } from "@shared/categories";
import { Loader2, Plus, Trash2, Search, Pencil } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface EntryLine {
  inventoryId: string;
  productId: string;
  productName: string;
  quantity: number;
  unitCost: string;
  batchNumber: string;
  expiryDate: string;
  manufactureDate: string;
}

export function EntryModal({
  show,
  onClose,
  onSuccess,
}: {
  show: boolean;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [step, setStep] = useState(1);
  const [isSaving, setIsSaving] = useState(false);
  const currentShop = getCurrentShop()!;
  const currentUser = getCurrentUser();
  const [productSearch, setProductSearch] = useState("");
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [showProductSearch, setShowProductSearch] = useState(false);

  // Step 1 fields
  const [supplierId, setSupplierId] = useState("");
  const [referenceType, setReferenceType] = useState("factura");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("confirmed");

  // Step 2 fields
  const [lines, setLines] = useState<EntryLine[]>([]);
  const [editingProduct, setEditingProduct] = useState<{
    inventoryId: string;
    productId: string;
    productName: string;
    quantity: number;
    unitCost: string;
    batchNumber: string;
    expiryDate: string;
    manufactureDate: string;
  } | null>(null);
  const [showAddProduct, setShowAddProduct] = useState(false);

  // Inline create product
  const [showCreateProduct, setShowCreateProduct] = useState(false);
  const [newProductCategory, setNewProductCategory] = useState("");
  const [newProductPrice, setNewProductPrice] = useState("");
  const [newProductCost, setNewProductCost] = useState("");
  const [isCreatingProduct, setIsCreatingProduct] = useState(false);

  // Supplier modal
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const queryClient = useQueryClient();

  const resetForm = () => {
    setStep(1);
    setSupplierId("");
    setReferenceType("factura");
    setReferenceNumber("");
    setNotes("");
    setStatus("confirmed");
    setLines([]);
    setProductSearch("");
    setSearchResults([]);
    setShowProductSearch(false);
    setEditingProduct(null);
    setShowAddProduct(false);
    setShowCreateProduct(false);
    setNewProductCategory("");
    setNewProductPrice("");
    setNewProductCost("");
    setIsCreatingProduct(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const searchProducts = async (q: string) => {
    setProductSearch(q);
    setShowCreateProduct(false);
    if (q.length < 1) {
      setSearchResults([]);
      return;
    }
    try {
      const data = await apiRequest<any>(
        `/api/shops/${currentShop.id}/inventory?search=${encodeURIComponent(q)}&limit=10`,
      );
      setSearchResults(Array.isArray(data) ? data : data?.data || []);
    } catch {
      setSearchResults([]);
    }
  };

  const addProductLine = (item: any) => {
    const exists = lines.find((l) => l.inventoryId === item.id);
    if (exists) {
      toast({ title: "Producto ya agregado", variant: "destructive" });
      return;
    }
    setEditingProduct({
      inventoryId: item.id,
      productId: item.productId,
      productName: item.product?.name || item.productName || "Producto",
      quantity: 1,
      unitCost: item.averageCost || item.acquisitionCost || "0",
      batchNumber: item.batchNumber || "",
      expiryDate: item.expiryDate || "",
      manufactureDate: item.manufactureDate || "",
    });
    setShowAddProduct(true);
    setShowProductSearch(false);
    setProductSearch("");
    setSearchResults([]);
  };

  const confirmAddProduct = () => {
    if (!editingProduct) return;
    if (editingProduct.quantity <= 0) {
      toast({ title: "La cantidad debe ser mayor a 0", variant: "destructive" });
      return;
    }
    if (!editingProduct.unitCost || parseFloat(editingProduct.unitCost) <= 0) {
      toast({ title: "El costo unitario es requerido", variant: "destructive" });
      return;
    }
    setLines((prev) => {
      const existing = prev.findIndex((l) => l.inventoryId === editingProduct.inventoryId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { ...editingProduct };
        return updated;
      }
      return [...prev, { ...editingProduct }];
    });
    setEditingProduct(null);
    setShowAddProduct(false);
  };

  const handleCreateAndAdd = async () => {
    const name = productSearch.trim();
    const category = newProductCategory;
    const cost = newProductCost || "0";
    if (!name || !category) {
      toast({ title: "Debe seleccionar una categoría", variant: "destructive" });
      return;
    }
    setIsCreatingProduct(true);
    try {
      const price = newProductPrice || "0";
      const result = await createProduct(currentShop.id, {
        name,
        category,
        price,
        stock: 0,
        threshold: 10,
        acquisitionCost: cost,
        supplierId: supplierId || undefined,
      });
      const newItem = result.inventory;
      setEditingProduct({
        inventoryId: newItem.id,
        productId: newItem.productId,
        productName: name,
        quantity: 1,
        unitCost: cost,
        batchNumber: "",
        expiryDate: "",
        manufactureDate: "",
      });
      setShowCreateProduct(false);
      setShowAddProduct(true);
      setSearchResults([]);
      setProductSearch("");
      setNewProductCategory("");
      setNewProductPrice("");
      setNewProductCost("");
      toast({ title: `Producto "${name}" creado y listo para agregar` });
    } catch (error: any) {
      toast({
        title: "Error al crear producto",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsCreatingProduct(false);
    }
  };

  const removeLine = (inventoryId: string) => {
    setLines((prev) => prev.filter((l) => l.inventoryId !== inventoryId));
  };

  const startEditLine = (inventoryId: string) => {
    const line = lines.find((l) => l.inventoryId === inventoryId);
    if (!line) return;
    setEditingProduct({ ...line });
    setShowAddProduct(true);
    setShowProductSearch(false);
  };

  const handleSave = async () => {
    if (lines.length === 0) {
      toast({ title: "Debe agregar al menos un producto", variant: "destructive" });
      return;
    }

    setIsSaving(true);
    try {
      await createEntry(currentShop.id, {
        supplierId: supplierId || undefined,
        referenceType,
        referenceNumber: referenceNumber || undefined,
        notes: notes || undefined,
        status,
        registeredById: currentUser?.id,
        confirmedById: status === "confirmed" ? currentUser?.id : undefined,
        lines: lines.map((l) => ({
          inventoryId: l.inventoryId,
          productId: l.productId,
          quantity: l.quantity,
          unitCost: l.unitCost,
          batchNumber: l.batchNumber || undefined,
          expiryDate: l.expiryDate || undefined,
          manufactureDate: l.manufactureDate || undefined,
        })),
      });

      toast({ title: "Entrada registrada exitosamente" });
      resetForm();
      onSuccess();
    } catch (error: any) {
      toast({
        title: "Error al registrar entrada",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? "📥 Nueva Entrada — Datos de recepción" : `📥 Nueva Entrada — Productos (${lines.length})`}
          </DialogTitle>
        </DialogHeader>

        {step === 1 && (
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <Label className="sm:text-right">Proveedor</Label>
              <div className="sm:col-span-3">
                    <SupplierSelector
                      shopId={currentShop.id}
                      value={supplierId}
                      onValueChange={setSupplierId}
                      label=""
                      placeholder="Seleccionar proveedor..."
                      onCreateSupplier={() => setShowSupplierModal(true)}
                    />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <Label className="sm:text-right">Tipo referencia</Label>
              <Select value={referenceType} onValueChange={setReferenceType}>
                <SelectTrigger className="sm:col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="factura">Factura</SelectItem>
                  <SelectItem value="guia">Guía de remisión</SelectItem>
                  <SelectItem value="interna">Nota interna</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <Label className="sm:text-right">Nº referencia</Label>
              <Input
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                className="sm:col-span-3"
                placeholder="FAC-2026-4589"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <Label className="sm:text-right">Notas</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="sm:col-span-3"
                placeholder="Notas adicionales..."
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-4 items-center gap-4">
              <Label className="sm:text-right">Estado</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="sm:col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">Confirmar inmediatamente</SelectItem>
                  <SelectItem value="draft">Guardar como borrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-4 py-4">
            {/* Product list — desktop table */}
            <div className="hidden md:block border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="p-2 text-left">#</th>
                    <th className="p-2 text-left">Producto</th>
                    <th className="p-2 text-right">Cant</th>
                    <th className="p-2 text-right">Costo Und</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2 text-center">Lote</th>
                    <th className="p-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, i) => (
                    <tr key={line.inventoryId} className="border-b">
                      <td className="p-2">{i + 1}</td>
                      <td className="p-2 font-medium">{line.productName}</td>
                      <td className="p-2 text-right">{line.quantity}</td>
                      <td className="p-2 text-right">${parseFloat(line.unitCost).toFixed(2)}</td>
                      <td className="p-2 text-right">${(line.quantity * parseFloat(line.unitCost)).toFixed(2)}</td>
                      <td className="p-2 text-center text-xs text-muted-foreground">{line.batchNumber || "—"}</td>
                      <td className="p-2">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => startEditLine(line.inventoryId)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => removeLine(line.inventoryId)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {lines.length === 0 && (
                    <tr>
                      <td colSpan={7} className="p-4 text-center text-muted-foreground">
                        No hay productos agregados
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Product list — mobile cards */}
            <div className="md:hidden space-y-2">
              {lines.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">No hay productos agregados</p>
              ) : (
                lines.map((line, i) => (
                  <div key={line.inventoryId} className="border rounded-lg p-3 space-y-1">
                    <div className="flex justify-between items-start">
                      <span className="font-medium text-sm">{line.productName}</span>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1" onClick={() => startEditLine(line.inventoryId)}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-6 w-6 -mt-1 -mr-1" onClick={() => removeLine(line.inventoryId)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-1 text-xs text-muted-foreground">
                      <div><span className="font-medium">Cant:</span> {line.quantity}</div>
                      <div><span className="font-medium">Und:</span> ${parseFloat(line.unitCost).toFixed(2)}</div>
                      <div><span className="font-medium">Total:</span> ${(line.quantity * parseFloat(line.unitCost)).toFixed(2)}</div>
                    </div>
                    {line.batchNumber && (
                      <div className="text-xs text-muted-foreground">
                        <span className="font-medium">Lote:</span> {line.batchNumber}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Add product section */}
            {!showAddProduct && !showCreateProduct && (
              <div className="relative">
                <div className="flex gap-2 flex-wrap">
                  <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      value={productSearch}
                      onChange={(e) => searchProducts(e.target.value)}
                      onFocus={() => setShowProductSearch(true)}
                      className="pl-9"
                      placeholder="Buscar producto para agregar..."
                    />
                  </div>
                  <Button onClick={() => setShowProductSearch(!showProductSearch)} variant="outline">
                    {showProductSearch ? "Cerrar" : "Buscar"}
                  </Button>
                </div>
                {showProductSearch && productSearch.length > 0 && !showCreateProduct && (
                  <div className="absolute z-10 w-full mt-1 border rounded-lg bg-background shadow-lg max-h-48 overflow-y-auto">
                    {searchResults.length === 0 ? (
                      <div className="p-3">
                        <p className="text-sm text-muted-foreground mb-2">
                          No se encontró "<strong>{productSearch}</strong>" en el catálogo
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            setShowCreateProduct(true);
                            setNewProductCategory("");
                            setNewProductPrice("");
                            setNewProductCost("");
                          }}
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Crear nuevo producto "{productSearch}"
                        </Button>
                      </div>
                    ) : (
                      searchResults.slice(0, 10).map((item: any) => (
                        <button
                          key={item.id}
                          onClick={() => addProductLine(item)}
                          className="w-full p-3 text-left hover:bg-muted flex flex-col sm:flex-row sm:justify-between items-start sm:items-center border-b last:border-0 gap-1"
                        >
                          <span className="font-medium">
                            {item.product?.name || item.productName || "Producto"}
                          </span>
                          <span className="text-sm text-muted-foreground whitespace-nowrap">
                            Stock: {item.stock} | Costo: ${parseFloat(item.averageCost || item.acquisitionCost || "0").toFixed(2)}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Create new product inline form */}
            {showCreateProduct && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <h4 className="font-medium flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Nuevo producto: {productSearch}
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Categoría *</Label>
                    <Select value={newProductCategory} onValueChange={setNewProductCategory}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar categoría..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {PRODUCT_CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Costo de adquisición (CUP) *</Label>
                    <Input
                      value={newProductCost}
                      onChange={(e) => setNewProductCost(e.target.value)}
                      placeholder="Costo de compra del producto"
                    />
                  </div>
                  <div>
                    <Label>Precio de venta (CUP)</Label>
                    <Input
                      value={newProductPrice}
                      onChange={(e) => setNewProductPrice(e.target.value)}
                      placeholder="Opcional, se puede ajustar después"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:justify-between items-center gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setShowCreateProduct(false);
                      setShowProductSearch(true);
                    }}
                  >
                    ← Volver a búsqueda
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowCreateProduct(false)}
                    >
                      Cancelar
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleCreateAndAdd}
                      disabled={isCreatingProduct || !newProductCategory}
                    >
                      {isCreatingProduct ? (
                        <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creando...</>
                      ) : (
                        "✓ Crear y agregar a entrada"
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Add product inline form */}
            {showAddProduct && editingProduct && (
              <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
                <h4 className="font-medium">{editingProduct.productName}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label>Cantidad recibida *</Label>
                    <Input
                      type="number"
                      min="1"
                      value={editingProduct.quantity}
                      onChange={(e) =>
                        setEditingProduct((prev) =>
                          prev ? { ...prev, quantity: Number(e.target.value) } : null,
                        )
                      }
                    />
                  </div>
                  <div>
                    <Label>Costo unitario (CUP) *</Label>
                    <Input
                      value={editingProduct.unitCost}
                      onChange={(e) =>
                        setEditingProduct((prev) =>
                          prev ? { ...prev, unitCost: e.target.value } : null,
                        )
                      }
                    />
                  </div>
                  <div>
                    <Label>Número de lote</Label>
                    <Input
                      value={editingProduct.batchNumber}
                      onChange={(e) =>
                        setEditingProduct((prev) =>
                          prev ? { ...prev, batchNumber: e.target.value } : null,
                        )
                      }
                      placeholder="LOTE-2026-XXX"
                    />
                  </div>
                  <div>
                    <Label>Fecha de caducidad</Label>
                    <Input
                      type="date"
                      value={editingProduct.expiryDate?.split("T")[0] || ""}
                      onChange={(e) =>
                        setEditingProduct((prev) =>
                          prev
                            ? { ...prev, expiryDate: e.target.value ? new Date(e.target.value).toISOString() : "" }
                            : null,
                        )
                      }
                    />
                  </div>
                </div>
                <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 pt-2">
                  <Button variant="outline" onClick={() => { setShowAddProduct(false); setEditingProduct(null); }}>
                    Cancelar
                  </Button>
                  <Button onClick={confirmAddProduct}>
                    ✓ {editingProduct && lines.some((l) => l.inventoryId === editingProduct.inventoryId) ? "Actualizar" : "Agregar"}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-col sm:flex-row sm:justify-between gap-2 pt-4 border-t">
          <div>
            {step === 2 && lines.length > 0 && (
              <div className="text-sm text-muted-foreground">
                {lines.length} producto(s) | {lines.reduce((s, l) => s + l.quantity, 0)} unidades | $
                {lines.reduce((s, l) => s + l.quantity * parseFloat(l.unitCost), 0).toFixed(2)}
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={handleClose} className="w-full sm:w-auto">Cancelar</Button>
            {step === 1 ? (
              <Button onClick={() => setStep(2)} disabled={!supplierId} className="w-full sm:w-auto">
                Continuar →
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={() => setStep(1)} className="w-full sm:w-auto">← Volver</Button>
                <Button onClick={handleSave} disabled={isSaving || lines.length === 0} className="w-full sm:w-auto">
                  {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Guardando...</> : "✅ Confirmar Entrada"}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
      <SupplierModal
        open={showSupplierModal}
        onOpenChange={setShowSupplierModal}
        shopId={currentShop.id}
        mode="create"
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ["suppliers", currentShop.id] });
          setShowSupplierModal(false);
        }}
      />
    </Dialog>
  );
}
